import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Card, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaSun, FaMoon } from 'react-icons/fa';
import { authAPI } from '../../utils/api';
import { jwtDecode } from 'jwt-decode';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('fieldflow-theme') || 'dark';
  });
  const navigate = useNavigate();

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('fieldflow-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use the authAPI client from our utils
      const response = await authAPI.login({ email, password });
      const data = response.data;

      // Store the token in localStorage
      localStorage.setItem('token', data.token);

      // Decode the JWT to get the authoritative user ID (sub-admins' login response
      // may return a parent admin ID in data.id; the signed JWT always has the correct value).
      let userId = data.id;
      try {
        const decoded = jwtDecode(data.token);
        userId = decoded.id ?? decoded.sub ?? decoded.userId ?? data.id;
      } catch (_) {}

      // Create user object from response data
      const user = {
        id: userId,
        name: data.name,
        email: data.email,
        role: data.role
      };
      
      // Store companyId if available
      if (data.companyId) {
        user.companyId = data.companyId;
      } else if (data.company_id) {
        user.companyId = data.company_id;
      }
      
      // Store permissions and fullAdminPrivileges if user is a sub-admin
      if (data.role === 'sub-admin') {
        if (data.permissions) {
          user.permissions = data.permissions;
        }
        if (data.fullAdminPrivileges !== undefined) {
          user.fullAdminPrivileges = data.fullAdminPrivileges;
        }
      }
      
      localStorage.setItem('user', JSON.stringify(user));

      // Dispatch custom event to notify App component of login
      window.dispatchEvent(new Event('userLoggedIn'));

      // Redirect based on user role
      if (data.role === 'super-admin') {
        navigate('/superadmin');
      } else if (data.role === 'admin' || data.role === 'sub-admin') {
        navigate('/admin');
      } else {
        navigate('/personnel');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Theme Toggle */}
      <button className="theme-toggle-login" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
        {theme === 'dark' ? <FaSun size={20} /> : <FaMoon size={20} />}
      </button>

      <Container className="login-container">
        <div className="login-wrapper">
          {/* Left Side - Branding */}
          <div className="login-branding">
            <div className="branding-content">
              <h1 className="brand-title">
                FieldFlow
              </h1>
              <p className="brand-subtitle">
                Field Operations Management System
              </p>
              <div className="brand-features">
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>Real-time GPS Tracking</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>Automated Reporting</span>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <span>Performance Analytics</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="login-form-wrapper">
            <Card className="login-card">
              <Card.Body>
                <div className="login-header">
                  <h2 className="login-title">Welcome Back</h2>
                  <p className="login-subtitle">Sign in to continue to FieldFlow</p>
                </div>
                
                {error && (
                  <Alert variant="danger" className="login-alert">
                    {error}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit} className="login-form">
                  <Form.Group className="mb-4" controlId="formBasicEmail">
                    <Form.Label>Email Address</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="input-icon">
                        <FaEnvelope />
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="form-input"
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formBasicPassword">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="input-icon">
                        <FaLock />
                      </InputGroup.Text>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="form-input"
                      />
                      <InputGroup.Text 
                        className="input-icon password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                  
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="login-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Form>

                <div className="login-footer">
                  <p className="footer-text">Secure login powered by FieldFlow</p>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Login;
